<script setup lang="ts">
import type { FormKitNode } from "@formkit/core";

const showMore = ref(false);
const onToggleShowMore = () => {
  showMore.value = !showMore.value;
};

const INITIAL_OPTIONS_AMOUNT = 5;
const inputRef = useTemplateRef<{
  node: FormKitNode;
}>("inputRef");
const showMoreOptions = computed(() => {
  return inputRef.value?.node.context?.options?.slice(INITIAL_OPTIONS_AMOUNT);
});

// Check if an option we're currently filtering on is under the show more button.
// If that's the case, open the filter
onMounted(() => {
  const inputValue = inputRef.value?.node._value;

  if (!inputValue) return;

  // Normalize query values to an array so we can use it to search
  const filteredValuesInArray = Array.isArray(inputValue)
    ? inputValue
    : [inputValue];

  // See if the query values are part of the show more options
  const optionValues = showMoreOptions.value?.map((option) => option.value);
  const optionIsFiltered = filteredValuesInArray.some((filteredValue) =>
    optionValues?.includes(filteredValue),
  );

  // If so, open the show more
  if (optionIsFiltered) {
    showMore.value = true;
  }
});

const getOptionLabel = (slotOption: unknown) => {
  if (!slotOption) return;

  if (typeof slotOption !== "object" || !("label" in slotOption)) return;

  return slotOption.label;
};

const getOptionCount = (slotOption: unknown) => {
  if (!slotOption) return;

  if (typeof slotOption !== "object" || !("count" in slotOption)) return;

  return slotOption.count;
};
</script>

<template>
  <FormKit
    ref="inputRef"
    v-bind="$attrs"
    :classes="{
      outer: '$reset',
      inner: '$reset p-1 flex items-center',
      options: 'flex flex-col gap-2 pt-2',
      option: showMore ? '' : `[&:nth-child(n+6)]:hidden`,
      wrapper: '$reset flex items-center mb-0 cursor-pointer',
      label: '$reset text-sm',
      legend: '$reset font-bold uppercase border-b w-full pb-1',
      fieldset: 'border-0',
    }"
  >
    <template #label="{ option }">
      <div class="flex items-end justify-between w-full">
        <span class="text-base">{{ getOptionLabel(option) }}</span>
        <span
          v-if="getOptionCount(option)"
          class="text-xs text-platform-text-2"
        >
          {{ getOptionCount(option) }}
        </span>
      </div>
    </template>
  </FormKit>
  <div v-if="showMoreOptions && showMoreOptions?.length > 0">
    <button
      class="uppercase mt-4 ml-1 hover:underline"
      :data-test="
        showMore
          ? 'filter-section-show-less-button'
          : 'filter-section-show-more-button'
      "
      @click="onToggleShowMore"
    >
      {{
        showMore ? `- ${$t("filter.showLess")}` : `+ ${$t("filter.showMore")}`
      }}
    </button>
  </div>
</template>
